export type ReviewStatus = "new" | "ai_tagged" | "needs_review" | "approved" | "excluded";

export type TagType = "geography" | "topic" | "policy_lever" | "population" | "exposure" | "evidence_type";

export type PaperTag = {
  id: string;
  type: TagType;
  label: string;
  slug: string;
  position: number;
};

export type TagAlias = {
  id: string;
  tagId: string;
  type: TagType;
  label: string;
  slug: string;
  createdAt: string;
  updatedAt: string;
};

export type AdminTag = {
  id: string;
  type: TagType;
  label: string;
  slug: string;
  entryCount: number;
  aliases: TagAlias[];
  createdAt: string;
  updatedAt: string;
};

export type Paper = {
  id: string;
  title: string;
  authors: string[];
  year: number | null;
  publication: string;
  sourceType: string;
  doi: string;
  url: string;
  abstract: string;
  geography: string;
  geographyTags: string[];
  geographicScope: string;
  countries: string[];
  region: string;
  topics: string[];
  policyTags: string[];
  populationTags: string[];
  exposureTags: string[];
  evidenceType: string;
  relevanceScore: number;
  reviewStatus: ReviewStatus;
  coreQuestion: string;
  keyFinding: string;
  summary: string;
  decisionRelevance: string;
  notes: string;
  tags: PaperTag[];
  createdAt: string;
  updatedAt: string;
};

export type PaperInput = Omit<Paper, "id" | "tags" | "createdAt" | "updatedAt">;

export type SourceArtifact = {
  id: string;
  sourceKind: string;
  sourceUrl: string;
  doi: string;
  filename: string;
  contentType: string;
  r2Key: string;
  sizeBytes: number;
  sourceMetadata: {
    title?: string;
    authors?: string[];
    year?: number | null;
    publication?: string;
    abstract?: string;
    notes?: string;
  };
  extractedText: string;
  extractedMarkdown: string;
  extractionStatus: string;
  extractionError: string;
  normalizedDoi: string;
  canonicalUrl: string;
  titleFingerprint: string;
  contentHash: string;
  duplicateOfType: string;
  duplicateOfId: string;
  duplicateConfidence: number;
  duplicateReason: string;
  createdAt: string;
  updatedAt: string;
};

export type SupportingExcerpt = {
  field: string;
  excerpt: string;
};

export type AiAnalysis = {
  id: string;
  sourceArtifactId: string;
  provider: string;
  model: string;
  status: string;
  confidence: number;
  draftEntry: PaperInput;
  supportingExcerpts: SupportingExcerpt[];
  analysisNotes: string;
  publishedEntryId: string;
  createdAt: string;
  updatedAt: string;
};

export type Stats = {
  total: number;
  approved: number;
  needsReview: number;
  geographies: number;
  avgRelevance: number;
};

export async function apiGet<T>(path: string): Promise<T> {
  const response = await fetch(path);
  if (!response.ok) throw new Error(await response.text());
  return response.json() as Promise<T>;
}

function storedAdminToken() {
  if (typeof window === "undefined") return "";
  return window.localStorage.getItem("lbr_admin_token") || "";
}

export async function apiSend<T>(path: string, method: "POST" | "PUT" | "DELETE", body?: unknown): Promise<T> {
  const headers = new Headers();
  if (body) headers.set("content-type", "application/json");

  const adminToken = storedAdminToken();
  if (adminToken) headers.set("authorization", `Bearer ${adminToken}`);

  const response = await fetch(path, {
    method,
    headers,
    body: body ? JSON.stringify(body) : undefined
  });
  if (response.status === 401) {
    window.localStorage.removeItem("lbr_admin_token");
  }
  if (!response.ok) throw new Error(await response.text());
  return response.json() as Promise<T>;
}
